import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from '@/lib/swal';
import { useCompleteExecution } from '../../hooks/useStepExecution';
import type { CompletionErrors } from '../../services/procedure-execution.service';

function extractCompletionErrors(error: Error): CompletionErrors | null {
  if (!axios.isAxiosError(error)) return null;
  const body = error.response?.data as
    | {
        error?: { details?: CompletionErrors; message?: string };
        message?: string;
        errors?: CompletionErrors;
      }
    | undefined;
  return body?.error?.details ?? body?.errors ?? null;
}

function buildErrorHtml(errors: CompletionErrors): string {
  const lines: string[] = [];

  if (errors.mandatoryIncomplete.length > 0) {
    lines.push(
      '<p class="font-semibold mb-1">Mandatory steps not completed:</p><ul class="text-left list-disc pl-4 space-y-0.5">',
    );
    for (const item of errors.mandatoryIncomplete) {
      lines.push(
        `<li><span class="text-muted-foreground">${escHtml(item.sectionTitle)} →</span> ${escHtml(item.stepTitle)}</li>`,
      );
    }
    lines.push('</ul>');
  }

  if (errors.evidenceInsufficient.length > 0) {
    lines.push(
      '<p class="font-semibold mt-3 mb-1">Evidence minimums not met:</p><ul class="text-left list-disc pl-4 space-y-0.5">',
    );
    for (const item of errors.evidenceInsufficient) {
      const parts: string[] = [];
      if (item.required.IMAGE) parts.push(`${item.actual.IMAGE}/${item.required.IMAGE} photos`);
      if (item.required.VIDEO) parts.push(`${item.actual.VIDEO}/${item.required.VIDEO} videos`);
      if (item.required.DOCUMENT)
        parts.push(`${item.actual.DOCUMENT}/${item.required.DOCUMENT} docs`);
      lines.push(`<li>${escHtml(item.stepTitle)}: ${parts.join(', ')}</li>`);
    }
    lines.push('</ul>');
  }

  if (errors.authorizationPending.length > 0) {
    lines.push(
      '<p class="font-semibold mt-3 mb-1">Authorizations still pending:</p><ul class="text-left list-disc pl-4 space-y-0.5">',
    );
    for (const item of errors.authorizationPending) {
      const label = item.authStatus === 'NONE' ? 'no request raised' : 'request pending';
      lines.push(
        `<li><span class="text-muted-foreground">${escHtml(item.sectionTitle)} →</span> ${escHtml(item.stepTitle)} <span class="text-xs text-muted-foreground">(${label})</span></li>`,
      );
    }
    lines.push('</ul>');
  }

  return lines.join('');
}

function escHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface Props {
  inspectionId: string;
  isCompleted: boolean;
  canExecute: boolean;
}

export function CompleteProcedureButton({ inspectionId, isCompleted, canExecute }: Props) {
  const mutation = useCompleteExecution(inspectionId);

  async function handleComplete() {
    const ok = await Swal.fire({
      title: 'Complete Procedure?',
      text: 'This will mark the procedure as completed. This action cannot be undone.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Complete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'hsl(74 40% 40%)',
      cancelButtonColor: 'hsl(74 15% 45%)',
      reverseButtons: true,
      allowOutsideClick: false,
      customClass: { popup: 'rounded-xl' },
    });

    if (!ok.isConfirmed) return;

    try {
      await mutation.mutateAsync();
      toast.success('Procedure completed successfully');
    } catch (e) {
      const errors = extractCompletionErrors(e as Error);
      if (
        errors &&
        (errors.mandatoryIncomplete.length > 0 ||
          errors.evidenceInsufficient.length > 0 ||
          errors.authorizationPending.length > 0)
      ) {
        await Swal.fire({
          title: 'Cannot Complete Procedure',
          html: `<div class="text-sm text-left">${buildErrorHtml(errors)}</div>`,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: 'hsl(74 40% 40%)',
          customClass: { popup: 'rounded-xl' },
        });
      } else {
        const axErr = axios.isAxiosError(e as Error);
        const body = axErr
          ? (
              e as Error & {
                response?: { data?: { error?: { message?: string }; message?: string } };
              }
            ).response?.data
          : undefined;
        const msg = body?.error?.message ?? body?.message ?? 'Failed to complete procedure';
        toast.error(msg);
      }
    }
  }

  if (isCompleted) {
    return (
      <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
        <span className="flex items-center gap-2 text-sm font-medium text-green-700">
          <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
          Procedure Completed
        </span>
      </div>
    );
  }

  if (!canExecute) return null;

  return (
    <div className="flex items-center justify-end px-5 py-4 border-t border-border">
      <button
        type="button"
        onClick={() => void handleComplete()}
        disabled={mutation.isPending}
        className="flex h-[44px] items-center gap-2 rounded-lg bg-primary px-5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
      >
        {mutation.isPending ? (
          <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
        ) : (
          <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
        )}
        Complete Procedure
      </button>
    </div>
  );
}
